// localStorage-backed auth store for customers. Kept in its own
// namespace so a logged-in professional in the same browser doesn't
// get mistaken for a customer (or vice versa).

const TOKEN_KEY = 'cherrypicked.customer.token';
const USER_KEY = 'cherrypicked.customer.user';

export type CustomerAuthUser = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUSPENDED' | 'DELETED';
  email_verified_at: string | null;
};

const listeners = new Set<() => void>();

function emit() {
  try {
    for (const l of listeners) {
      try {
        l();
      } catch {
        /* one bad listener shouldn't break the others */
      }
    }
  } catch (e) {
    console.error('Error emitting customer auth change:', e);
  }
}

export function getCustomerToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch (e) {
    console.error('Error getting customer token:', e);
    return null;
  }
}

// useSyncExternalStore compares snapshots with Object.is — if we returned
// a freshly-parsed object on every call, React would loop forever. Cache
// the parsed value and re-parse only when the raw JSON string changes.
let customerCache: { raw: string | null; parsed: CustomerAuthUser | null } = {
  raw: null,
  parsed: null,
};

export function getCustomer(): CustomerAuthUser | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(USER_KEY);
    if (raw === customerCache.raw) return customerCache.parsed;
    const parsed = raw ? (JSON.parse(raw) as CustomerAuthUser) : null;
    customerCache = { raw, parsed };
    return parsed;
  } catch (e) {
    console.error('Error getting customer:', e);
    return null;
  }
}

export function setCustomerAuth(token: string, user: CustomerAuthUser) {
  try {
    // Only one role can hold an active session at a time. Wipe any pro
    // token sitting in the same browser so guards and headers don't
    // see both flows authenticated. Direct localStorage poke (rather
    // than importing clearAuth) avoids a circular dep.
    window.localStorage.removeItem('cherrypicked.pro.token');
    window.localStorage.removeItem('cherrypicked.pro.user');
    window.localStorage.setItem(TOKEN_KEY, token);
    window.localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch (e) {
    console.error('Error setting customer auth:', e);
  } finally {
    emit();
  }
}

export function patchCustomer(patch: Partial<CustomerAuthUser>) {
  const current = getCustomer();
  if (!current) return;
  const next = { ...current, ...patch };
  try {
    window.localStorage.setItem(USER_KEY, JSON.stringify(next));
  } catch (e) {
    console.error('Error patching customer:', e);
  } finally {
    emit();
  }
}

export function clearCustomerAuth() {
  try {
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem(USER_KEY);
  } catch (e) {
    console.error('Error clearing customer auth:', e);
  } finally {
    emit();
  }
}

export function subscribeCustomer(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isCustomerAuthenticated(): boolean {
  return !!getCustomerToken();
}
